import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { ExemptionEntry } from "../../../../../api/declarationsApi"

/** The exemption field's spec key (RoHS `exemption_number`). */
export const EXEMPTION_FIELD = "exemption_number"

/** The dropdown choice that switches the field to free text. Never stored. */
export const OTHER_EXEMPTION = "__other__"

const BARE_CODE_RE = /^(\d{1,2})([a-z])(?=$|-)/

/** Twin of the backend's `normalize_exemption_code`: spaces dropped, case folded, the
 *  bare "6c" notation bridged to "6(c)". Matching only, never a stored value. */
export function normalizeExemptionCode(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null
  const folded = raw.replace(/\s+/g, "").toLowerCase()
  return folded.replace(BARE_CODE_RE, "$1($2)") || null
}

/** The register entry a stored code names, by the shared normalization only, exactly as
 *  approve resolves it ("Ausnahme 6c" names none). Twin: `register_entry` in the
 *  backend's `mc_exemption_suggestions.py`. */
export function registerEntry(
  raw: string | null | undefined,
  entries: readonly ExemptionEntry[],
): ExemptionEntry | null {
  const code = normalizeExemptionCode(raw)
  if (!code) return null
  return (
    entries.find((e) => normalizeExemptionCode(e.entryNumber) === code) ?? null
  )
}

// Twins of `_LETTERED_CODE_RE` / `_LABELLED_BARE_RE` in the backend's
// `mc_exemption_suggestions.py`: keep the two in step.
const LETTERED_CODE_RE =
  /(?<![\d.])(\d{1,2})\s*\(?\s*([a-z])\s*\)?(?:\s*-\s*([ivx]+))?(?![a-z\d])/gi
const LABELLED_BARE_RE = /^[^\d]*?(\d{1,2})\s*$/
const LIST_SEPARATOR_RE = /[,;&+]|\b(?:und|and)\b/i

/** Every register entry a free text names ("Ausnahme 6c", "6(c), 7(a)", "6(c) bis
 *  2024"). A lettered code counts wherever it stands; a bare number (15, 24) only when it
 *  is a whole list item behind a label ("6(c), 15"), so a date or a year never reads as an entry. For
 *  what a reviewer types, never for judging a stored value. Twin: `entries_in_text`. */
export function entriesInText(
  raw: string | null | undefined,
  entries: readonly ExemptionEntry[],
): ExemptionEntry[] {
  const found: ExemptionEntry[] = []
  for (const m of (raw ?? "").matchAll(LETTERED_CODE_RE)) {
    const code = `${m[1]}(${m[2]})${m[3] ? `-${m[3]}` : ""}`
    const entry = registerEntry(code, entries)
    if (entry && !found.includes(entry)) found.push(entry)
  }
  for (const item of (raw ?? "").split(LIST_SEPARATOR_RE)) {
    const bare = LABELLED_BARE_RE.exec(item)
    const entry = bare ? registerEntry(bare[1], entries) : null
    if (entry && !found.includes(entry)) found.push(entry)
  }
  return found
}

/** "6(c) · Lead in copper alloy … · expired": the entry, its scope, and its status
 *  when it is not plainly active, so an expired or renewal-pending entry is marked,
 *  never hidden. */
export function exemptionLabel(
  e: ExemptionEntry,
  translate: (key: TranslationKeys) => string,
): string {
  const parts = [e.entryNumber]
  if (e.substanceScope) parts.push(e.substanceScope)
  if (e.status === "expired") {
    parts.push(`⚠ ${translate("MC_DECL_V2_EXEMPTION_STATUS_EXPIRED")}`)
  } else if (e.status === "renewal_pending") {
    parts.push(translate("MC_DECL_V2_EXEMPTION_STATUS_RENEWAL"))
  }
  return parts.join(" · ")
}

/** The warning a stored exemption value carries, or none: not a register entry at all, or
 *  an entry that has expired (a renewal-pending one is still in force). */
export function exemptionWarning(
  entry: ExemptionEntry | null,
  value: string,
  translate: (key: TranslationKeys, v?: Record<string, string>) => string,
): string | undefined {
  if (!entry) return translate("MC_DECL_V2_EXEMPTION_INVALID", { value })
  if (entry.status === "expired") {
    return translate("MC_DECL_V2_EXEMPTION_SAVED_EXPIRED", {
      code: entry.entryNumber,
    })
  }
  return undefined
}
